import React, { useContext } from 'react';
import { Field, useFormikContext } from 'formik';

import { FormRow, HelperMessage, Divider } from 'shared-components/components';
import { ToggleButtonGroupField } from 'shared-components/components/FormikComponents';

import { getLastUpdateUser } from '../../../helpers';
import { LastUpdateUserDetails } from '../../../shared/interfaces';
import { preCtChartCheckContent, yesNKAOptions } from '../../constants';
import FormContext from '../../contexts';
import AllergyCardGroupField from './AllergyCardGroupField';
import { yesNoOptions } from 'op-utils/helpers';

interface Props {
  assessmentId: string;
  handleMutation: any;
  lastUpdateUserData: LastUpdateUserDetails[];
}

const Allergies = ({ assessmentId, handleMutation, lastUpdateUserData }: Props): JSX.Element => {
  const { setFieldValue, setFieldTouched, values }: any = useFormikContext();
  const formContext = useContext(FormContext);
  const { createAllergy, deleteAllergy } = formContext.mutations;

  const { hasPatientAllergies, pcccAllergy, carriesOwnAutoInjector, hasAsciaManagementPlan } =
    preCtChartCheckContent.allergies.fields;
  const { infoHelpers, actionLabels } = preCtChartCheckContent.allergies;

  const hasAnaphylaxisReaction = () => {
    return values.pcccAllergy?.some((allergy: any) =>
      allergy.pcccReaction?.some((reaction: any) => reaction.baseValue === 'Anaphylaxis'),
    );
  };

  const handleHasAllergiesChange = (value: boolean) => {
    if (value === true && values.pcccAllergy.length === 0) {
      createAllergy({
        variables: { assessmentId, allergyType: '', additionalDetails: '' },
      }).then(({ data }: { data: any }) => {
        const firstAllergy = data.createPcccAllergy.preCtChartCheck.pcccAllergy[0];
        setFieldValue('pcccAllergy', [firstAllergy]);
      });
    }
    if (value === false && values.pcccAllergy.length > 0) {
      values.pcccAllergy.forEach((item: any) => {
        deleteAllergy({ variables: { id: item.id } });
      });
      setFieldValue('pcccAllergy', []);
      setFieldTouched('pcccAllergy', []);
    }
  };

  return (
    <>
      <FormRow fieldLabel={hasPatientAllergies} fieldName={'hasPatientAllergies'}>
        <Field
          name="hasPatientAllergies"
          component={ToggleButtonGroupField}
          label={hasPatientAllergies}
          options={yesNKAOptions}
          handleMutation={handleMutation}
          conditionalHandleChange={handleHasAllergiesChange}
          lastUpdateUser={getLastUpdateUser(lastUpdateUserData, 'hasPatientAllergies')}
        />
        {values.hasPatientAllergies === false && (
          <HelperMessage fieldName={'hasPatientAllergies'} fieldText={infoHelpers.allergyAlert} helperType="info" />
        )}
      </FormRow>
      {values.hasPatientAllergies === true && (
        <>
          <Divider />
          <AllergyCardGroupField
            name="pcccAllergy"
            label={pcccAllergy}
            actionButtonLabel={actionLabels.addAllergy}
            assessmentId={assessmentId}
          />

          {hasAnaphylaxisReaction() && (
            <>
              <FormRow fieldLabel={carriesOwnAutoInjector} fieldName={'carriesOwnAutoInjector'}>
                <Field
                  name="carriesOwnAutoInjector"
                  component={ToggleButtonGroupField}
                  label={carriesOwnAutoInjector}
                  options={yesNoOptions}
                  handleMutation={handleMutation}
                  lastUpdateUser={getLastUpdateUser(lastUpdateUserData, 'carriesOwnAutoInjector')}
                />
                {values.carriesOwnAutoInjector === true && (
                  <HelperMessage
                    fieldName={'carriesOwnAutoInjector'}
                    fieldText={infoHelpers.carriesOwnAutoInjectorAlert}
                    helperType="info"
                  />
                )}
              </FormRow>

              <FormRow fieldLabel={hasAsciaManagementPlan} fieldName={'hasAsciaManagementPlan'}>
                <Field
                  name="hasAsciaManagementPlan"
                  component={ToggleButtonGroupField}
                  label={hasAsciaManagementPlan}
                  options={yesNoOptions}
                  handleMutation={handleMutation}
                  lastUpdateUser={getLastUpdateUser(lastUpdateUserData, 'hasAsciaManagementPlan')}
                />
                {values.hasAsciaManagementPlan === true && (
                  <HelperMessage
                    fieldName={'hasAsciaManagementPlan'}
                    fieldText={infoHelpers.hasAsciaManagementPlanAlert}
                    helperType="info"
                  />
                )}
              </FormRow>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Allergies;
